import React, { createContext, useContext } from 'react';

/**
 * Where portalled UI — the confirmation modal, the section-link context menu — is
 * rendered.
 *
 * `document.body` is right for the hosted app, and wrong for an embedded viewer: inside a
 * shadow root, a portal to `document.body` escapes the boundary and lands in the host
 * page, unstyled by the viewer's stylesheet and sitting in the host's DOM. The embedded
 * viewer supplies a container of its own inside the shadow root instead.
 *
 * `undefined` means no provider (the hosted app); `null` means a provider whose container
 * has not attached yet. The two must stay distinct, or an embed would portal into the
 * host's body for one render.
 */
const PortalContainerContext = createContext<HTMLElement | null | undefined>(undefined);

export const PortalContainerProvider: React.FC<{
    container: HTMLElement | null;
    children: React.ReactNode;
}> = ({ container, children }) => (
    <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>
);

/**
 * The portal target, or `null` when there is nothing to portal into yet. Callers render
 * nothing until it exists.
 */
export function usePortalContainer(): HTMLElement | null {
    const container = useContext(PortalContainerContext);
    if (container !== undefined) {
        return container;
    }
    return typeof document !== 'undefined' ? document.body : null;
}
